import { Box, Typography } from "@mui/material";
import * as React from "react";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ResizeObserver from "resize-observer-polyfill";
import ShowAt from "../../../components/common/ShowAt/ShowAt.tsx";
import { useAppStore } from "../../../hooks/useStores.tsx";
import { DesignEditorContext } from "../../contexts/DesignEditor.tsx";
import { Context } from "../../contexts/context";
import { Editor } from "../../core";
import UseAdsEditorUtils from "../../hooks/useAdsEditorUtils.tsx";
import { useEditor } from "../../hooks/useEditor";
import useIsSidebarOpen from "../../hooks/useIsSidebarOpen.tsx";
import type { EditorConfig } from "../../types";
import styles from "./canvas.module.scss";

export interface ICanvasProps {
	config?: Partial<EditorConfig>;
	id?: string;
}

export const Canvas = (props: ICanvasProps) => {
	const editor = useEditor();
	const context = React.useContext(Context);
	const { isPopupView, setCurrentScene } = useContext(DesignEditorContext);
	const { uiStore, campaignStore, brandsDefinitionStore, imagesStore } = useAppStore();
	const containerRef = React.useRef<HTMLDivElement>(null);
	const isSidebarOpen = useIsSidebarOpen();
	const [buttonPosition, setButtonPosition] = React.useState({ left: 0, top: 0 });
	const { t } = useTranslation("translation", { keyPrefix: "editor.controllers.canvasControllers" });
	const adsEditorUtils = UseAdsEditorUtils();

	if (editor) {
		editor.scene.name =
			editor?.scene.name && editor?.scene.name != t("untitledDesign")
				? editor?.scene.name
				: t("Untitled") + " " + editor?.frame?.frame?.width + "x" + editor?.frame?.frame?.height;
	}

	useEffect(() => {
		const container = containerRef.current as HTMLDivElement;
		const { clientHeight, clientWidth } = container;
		const editor = new Editor({
			id: props.id ?? "bria_canvas",
			config: {
				...props.config,
				size: {
					width: clientWidth,
					height: clientHeight,
				},
			},
			state: context,
			adsEditorUtils: adsEditorUtils,
			campaignStore: campaignStore,
			imagesStore: imagesStore,
			setCurrentScn: setCurrentScene,
		});

		const resizeObserver = new ResizeObserver((entries) => {
			const { width = clientWidth, height = clientHeight } = (entries[0] && entries[0].contentRect) || {};
			editor.canvas.resize({
				width,
				height,
			});
			updateButtonPosition(width, height);
		});
		resizeObserver.observe(container);
		return () => {
			editor.destroy();
			if (container) {
				resizeObserver.unobserve(container);
			}
		};
	}, []);

	// commented this code  due to Scene context is loading before all elements are fully initialized.

	// useEffect(() => {
	// 	const updateCurrentScene = async () => {
	// 		if (editor) {
	// 			const updatedTemplate = editor.scene.exportToJSON();
	// 			const updatedPreview = await editor.renderer.render(updatedTemplate);

	// 			const updatedScenes = scenes.map((scene) => {
	// 				if (scene.id === updatedTemplate.id) {
	// 					return { ...updatedTemplate, preview: updatedPreview };
	// 				}
	// 				return scene;
	// 			}) as IScene[];
	// 			setScenes(updatedScenes);
	// 		}
	// 	};
	// 	!isPopupView && updateCurrentScene();
	// }, [editedLabel]);

	useEffect(() => {
		updateButtonPosition(
			editor?.canvas?.getConfig().size?.width ?? 0,
			editor?.canvas?.getConfig().size?.height ?? 0,
		);
	}, [editor?.canvas?.canvas?.viewportTransform?.[0]]);

	const updateButtonPosition = (width: number, height: number) => {
		if (editor && editor.canvas && editor.frame) {
			const viewportTransform = editor.canvas.canvas?.viewportTransform;

			if (viewportTransform) {
				const newLeft =
					width / 2 - (viewportTransform[0] * editor.frame.frame.width) / 2 - (isPopupView ? 160 : 0);
				const newTop = height / 2 - (viewportTransform[3] * editor.frame.frame.height) / 2;
				setButtonPosition({ left: newLeft, top: newTop });
			}
		}
	};

	const getSceneLbelPosition = () => {
		const { left, top } = buttonPosition;

		if (isPopupView) {
			return {
				left: !uiStore.showGuideSection ? `${left}px` : `${left - 160}px`,
				top: `${top - 35}px`,
			};
		} else {
			const value = isSidebarOpen ? 560 : 722;
			return {
				left: !uiStore.showGuideSection ? `${left + value}px` : `${left}px`,
				top: `${top + 140}px`,
			};
		}
	};

	return (
		<Box component={"div"} className={styles.container} id="bria_canvas_container" ref={containerRef}>
			<Box component={"div"} className={styles.canvasWrapper}>
				<canvas id={props.id ?? "bria_canvas"}></canvas>
			</Box>
			<ShowAt>
				<Box className={styles.canvasHeader} style={getSceneLbelPosition()}>
					{isPopupView && (
						<Typography className={styles.canvasInfo}>
							{t("template")}:{" "}
							<Box component="span" fontWeight="bold">
								{campaignStore.selectedWizardTemplate?.name}
							</Box>
							{campaignStore?.selectedWizardTemplate?.is_branded_template && (
								<>
									{" | "}
									{t("brand")}:{" "}
									<Box component="span" fontWeight="bold">
										{brandsDefinitionStore.selectedBrand.name}
									</Box>
								</>
							)}
						</Typography>
					)}
				</Box>
			</ShowAt>
		</Box>
	);
};
